import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Progress,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { BsDownload, BsFileEarmarkExcel, BsTrash, BsUpload } from "react-icons/bs";
import templateApi from "../api/templateApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, PageSkeleton } from "../components/common/PageState";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDateTime, unwrapDetail } from "../utils/normalize";

const steps = ["Upload", "Parse", "Detected structure", "Mapping", "Validation", "Confirm"];
const processingStages = [
  "Uploading workbook",
  "Reading sheets",
  "Detecting rating scale",
  "Detecting sections",
  "Detecting items",
  "Detecting workflow",
  "Validating template",
];

const fileSize = (file) => {
  if (!file) return "";
  const size = file.size / 1024 / 1024;
  return `${size.toFixed(size >= 10 ? 0 : 1)} MB`;
};

const PerformanceTemplateImportPage = () => {
  const { importId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [file, setFile] = React.useState(null);
  const [activeStep, setActiveStep] = React.useState(importId ? 2 : 0);
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const dropzoneBg = useColorModeValue("gray.50", "gray.900");
  const mutedText = useColorModeValue("gray.600", "gray.400");

  const importQuery = useQuery({
    queryKey: performanceQueryKeys.imports.detail(importId),
    queryFn: () => templateApi.getPerformanceTemplateImport(importId),
    enabled: Boolean(importId),
  });

  const uploadMutation = useMutation({
    mutationFn: templateApi.uploadPerformanceTemplateExcel,
    onSuccess: (response) => {
      const detail = unwrapDetail(response);
      const nextImportId = detail?._id || detail?.id || detail?.importId;
      toast({ title: "Workbook uploaded", status: "success", duration: 2000 });
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.imports.all });
      if (nextImportId) {
        navigate(`/performance/pms-format/imports/${nextImportId}`, { replace: true });
      }
      setActiveStep(1);
    },
    onError: (error) =>
      toast({
        title: "Upload failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
      }),
  });

  const validateMutation = useMutation({
    mutationFn: () => templateApi.validatePerformanceTemplateImport(importId),
    onSuccess: () => {
      toast({ title: "Import validation completed", status: "success", duration: 2000 });
      setActiveStep(4);
      importQuery.refetch();
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => templateApi.confirmPerformanceTemplateImport(importId),
    onSuccess: (response) => {
      const detail = unwrapDetail(response);
      toast({ title: "Draft template created", status: "success", duration: 2000 });
      navigate(`/performance/pms-format/templates/${detail?.templateId || detail?._id || ""}`);
    },
  });

  const onDrop = React.useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length) {
        toast({ title: "Please select a valid .xlsx file.", status: "warning", duration: 2200 });
        return;
      }
      setFile(acceptedFiles[0] || null);
    },
    [toast],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  const detail = unwrapDetail(importQuery.data);

  return (
    <ModulePageShell
      title="Import template from Excel"
      description="Upload an Excel workbook to create a draft appraisal template."
      actions={<Button size="sm" variant="outline" leftIcon={<BsDownload />}>Download sample format</Button>}
    >
      <PerformanceLocalNav />
      <VStack align="stretch" spacing={4}>
        <OperationalPanel>
          <Stepper size="sm" index={activeStep} colorScheme="blue">
            {steps.map((step) => (
              <Step key={step}>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <StepTitle>{step}</StepTitle>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
        </OperationalPanel>

        <OperationalPanel>
          <Box
            {...getRootProps()}
            borderWidth="1px"
            borderStyle="dashed"
            borderColor={isDragActive ? "blue.400" : borderColor}
            borderRadius="lg"
            p={8}
            bg={dropzoneBg}
            textAlign="center"
            cursor="pointer"
          >
            <input {...getInputProps()} />
            <VStack spacing={3}>
              <BsFileEarmarkExcel size={32} />
              <Box>
                <Text fontWeight="semibold">{file ? file.name : "Drop an .xlsx workbook here"}</Text>
                <Text fontSize="sm" color={mutedText}>
                  {file ? fileSize(file) : "Drag-and-drop or browse to choose a file."}
                </Text>
              </Box>
              <Button size="sm" variant="outline" leftIcon={<BsUpload />}>
                {file ? "Replace file" : "Browse"}
              </Button>
            </VStack>
          </Box>
          {file ? (
            <Flex justify="space-between" align="center" mt={3} gap={3} wrap="wrap">
              <Text fontSize="sm" color={mutedText}>
                Selected file: {file.name} · {fileSize(file)}
              </Text>
              <HStack>
                <IconButton size="sm" variant="ghost" icon={<BsTrash />} aria-label="Remove selected file" onClick={() => setFile(null)} />
                <Button size="sm" variant="outline" onClick={() => setActiveStep(4)} isDisabled={!file}>
                  Validate file
                </Button>
                <Button size="sm" colorScheme="blue" onClick={() => uploadMutation.mutate(file)} isLoading={uploadMutation.isPending}>
                  Import as draft
                </Button>
              </HStack>
            </Flex>
          ) : null}
        </OperationalPanel>

        {(uploadMutation.isPending || validateMutation.isPending) ? (
          <OperationalPanel>
            <VStack align="stretch" spacing={3}>
              <Progress size="sm" isIndeterminate colorScheme="blue" />
              {processingStages.map((stage) => (
                <Text key={stage} fontSize="sm" color={mutedText}>
                  {stage}
                </Text>
              ))}
            </VStack>
          </OperationalPanel>
        ) : null}

        {importId ? (
          <OperationalPanel>
            {importQuery.isLoading ? (
              <PageSkeleton rows={3} />
            ) : !detail ? (
              <CompactEmptyState title="Import record not found." />
            ) : (
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                  <Box>
                    <Text fontWeight="semibold">{detail.fileName || detail.originalName || "Workbook import"}</Text>
                    <Text fontSize="sm" color={mutedText}>
                      Imported {formatDateTime(detail.createdAt)}
                    </Text>
                  </Box>
                  <StatusBadge value={detail.status || "UPLOADED"} />
                </Flex>
                <TableContainer>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Detected structure</Th>
                        <Th>Value</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td>Rating scale</Td>
                        <Td>{detail.detectedRatingScale?.name || detail.ratingScaleName || "-"}</Td>
                      </Tr>
                      <Tr>
                        <Td>Sections</Td>
                        <Td>{detail.detectedSections?.length || detail.sections?.length || 0}</Td>
                      </Tr>
                      <Tr>
                        <Td>Items</Td>
                        <Td>{detail.detectedItems?.length || detail.items?.length || 0}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>
                <HStack justify="flex-end">
                  <Button size="sm" variant="outline" onClick={() => validateMutation.mutate()} isLoading={validateMutation.isPending}>
                    Validate import
                  </Button>
                  <Button size="sm" colorScheme="blue" onClick={() => confirmMutation.mutate()} isLoading={confirmMutation.isPending}>
                    Confirm draft
                  </Button>
                </HStack>
              </VStack>
            )}
          </OperationalPanel>
        ) : null}
      </VStack>
    </ModulePageShell>
  );
};

export default PerformanceTemplateImportPage;
